'use client';

import { useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Textarea } from '@kit/ui/textarea';

import {
  AffiliateApplicationData,
  AffiliateApplicationSchema,
  AudienceSizeOptions,
  CountryOptions,
  NicheOptions,
} from '../_lib/affiliate-schema';
import { submitAffiliateApplication } from '../_lib/server/affiliate-actions';

const socialPlatforms = [
  'Twitter/X',
  'Instagram',
  'TikTok',
  'YouTube',
  'OnlyFans',
  'Reddit',
  'Telegram',
  'Discord',
  'Website/Blog',
  'Other',
];

export function AffiliateApplicationForm() {
  const [pending, startTransition] = useTransition();
  const [state, setState] = useState<{
    success: boolean;
    error: string | null;
  }>({
    success: false,
    error: null,
  });

  const form = useForm<AffiliateApplicationData>({
    resolver: zodResolver(AffiliateApplicationSchema),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      country: '',
      websiteUrl: '',
      socialMediaLinks: [],
      audienceSize: '',
      niche: '',
      promotionMethods: '',
      whyJoin: '',
      termsAccepted: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'socialMediaLinks',
  });

  if (state.success) {
    return (
      <Alert variant="default" className="border-green-500/50 bg-green-500/10">
        <AlertTitle className="text-green-400">
          Application Submitted Successfully!
        </AlertTitle>
        <AlertDescription className="text-gray-300">
          Thank you for your interest in becoming an Edem AI affiliate. We will
          review your application and get back to you within 2-3 business days.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit((data) => {
          startTransition(async () => {
            try {
              await submitAffiliateApplication(data);
              setState({ success: true, error: null });
            } catch (err) {
              setState({
                success: false,
                error:
                  err instanceof Error
                    ? err.message
                    : 'Failed to submit application',
              });
            }
          });
        })}
      >
        {state.error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">
            Personal Information
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John Doe"
                      className="border-gray-700 bg-gray-800/50"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address *</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      className="border-gray-700 bg-gray-800/50"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      className="border-gray-700 bg-gray-800/50"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="border-gray-700 bg-gray-800/50">
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CountryOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Online Presence */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Online Presence</h3>

          <FormField
            control={form.control}
            name="websiteUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website URL</FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder="https://yourwebsite.com"
                    className="border-gray-700 bg-gray-800/50"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="text-gray-500">
                  Your personal website or blog (if any)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Social Media Links */}
          <div className="space-y-2">
            <FormLabel>Social Media Profiles</FormLabel>
            <FormDescription className="text-gray-500">
              Add your social media profiles (optional but recommended)
            </FormDescription>

            {fields.map((field, index) => (
              <div key={field.id} className="flex flex-col gap-2 sm:flex-row">
                <Select
                  value={form.watch(`socialMediaLinks.${index}.platform`)}
                  onValueChange={(value) =>
                    form.setValue(`socialMediaLinks.${index}.platform`, value)
                  }
                >
                  <SelectTrigger className="w-full border-gray-700 bg-gray-800/50 sm:w-40">
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {socialPlatforms.map((platform) => (
                      <SelectItem key={platform} value={platform}>
                        {platform}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="https://..."
                  className="flex-1 border-gray-700 bg-gray-800/50"
                  {...form.register(`socialMediaLinks.${index}.url`)}
                />

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  className="text-gray-400 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ platform: '', url: '' })}
              className="mt-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Social Media
            </Button>
          </div>
        </div>

        {/* Experience & Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">
            Experience & Details
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="audienceSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Audience Size *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="border-gray-700 bg-gray-800/50">
                        <SelectValue placeholder="Select audience size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AudienceSizeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-gray-500">
                    Combined followers across all platforms
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="niche"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary Niche *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="border-gray-700 bg-gray-800/50">
                        <SelectValue placeholder="Select your niche" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {NicheOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="promotionMethods"
            render={({ field }) => (
              <FormItem>
                <FormLabel>How do you plan to promote Edem AI? *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe your promotion strategy (e.g., social media posts, video reviews, blog articles, email newsletters...)"
                    className="min-h-24 border-gray-700 bg-gray-800/50"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="whyJoin"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Why do you want to join our affiliate program?
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Tell us about yourself and why you're interested in partnering with Edem AI..."
                    className="min-h-24 border-gray-700 bg-gray-800/50"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Terms */}
        <FormField
          control={form.control}
          name="termsAccepted"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-y-0 space-x-3 rounded-lg border border-gray-700 p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>I agree to the terms and conditions *</FormLabel>
                <FormDescription className="text-gray-500">
                  By submitting this application, you agree to our affiliate
                  terms of service. You confirm you are at least 18 years old
                  and will comply with our promotional guidelines.
                </FormDescription>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <p className="text-center text-sm text-amber-400">
          Our affiliate program is currently under development. Applications
          will open soon!
        </p>

        <Button
          type="submit"
          disabled
          className="from-eros-orange via-eros-pink to-eros-purple w-full cursor-not-allowed bg-gradient-to-r opacity-50"
          size="lg"
        >
          Coming Soon
        </Button>
      </form>
    </Form>
  );
}
